// V14.10 — Unified Logs/Responses tab.
//
// Aggregates two kinds of records into one stream for controllers:
//   1. Activity logs (manual entries from the Log Entry panel — img1 in spec)
//   2. Inject responses (notes posted against an inject)
//
// Each card supports a threaded reply input (text + attachments) and a
// "View source" action that opens the originating inject / activity stream.
// Visibility is enforced by the parent tab gate (simcell + super_admin).
//
// Reply history for activity logs is stored as `replies[]` on the log doc.
// Reply history for inject responses reuses the existing inject notes
// dialog (each new note in that thread *is* a reply).

import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { Textarea } from '../../ui/textarea';
import { MessageSquare, Send, Phone, Mail, Radio, FileCheck, AlertTriangle, MoreHorizontal, ExternalLink, Paperclip, Loader2, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { activityLogsApi, uploadsApi } from '../../../lib/api';
import { UploadHint, UPLOAD_ACCEPT_ATTR } from '../../UploadHint';

const ACTIVITY_ICONS = {
  phone_call: Phone,
  email: Mail,
  radio_message: Radio,
  verbal_communication: MessageSquare,
  decision: FileCheck,
  external_event: AlertTriangle,
  other: MoreHorizontal,
};

function ActivityIcon({ type, className = 'w-3.5 h-3.5' }) {
  const Icon = ACTIVITY_ICONS[type] || MessageSquare;
  return <Icon className={className} />;
}

function formatTime(ts, timezone = 'UTC') {
  if (!ts) return '-';
  try {
    return new Date(ts).toLocaleString('en-GB', {
      timeZone: timezone,
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(ts); }
}

// ──────────────────────────────────────────────────────────────────────
// Activity log card (with inline reply thread)
// ──────────────────────────────────────────────────────────────────────
function ActivityLogCard({ log, getFileUrl, onReplyAdded, timezone }) {
  const [replyText, setReplyText] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState((log.replies || []).length > 0);

  const submitReply = async (e) => {
    e?.preventDefault?.();
    if (!replyText.trim()) return;
    setSending(true);
    try {
      let attachmentIds = [];
      for (const f of replyFiles) {
        if (f.size > 25 * 1024 * 1024) throw new Error(`File too large: ${f.name}`);
        const res = await uploadsApi.upload(f);
        attachmentIds.push(res.file_id);
      }
      const reply = await activityLogsApi.addReply(log.log_id, replyText.trim(), attachmentIds);
      toast.success('Reply added');
      setReplyText('');
      setReplyFiles([]);
      onReplyAdded?.(log.log_id, reply);
      setExpanded(true);
    } catch (err) {
      toast.error(err.message || 'Failed to add reply');
    } finally {
      setSending(false);
    }
  };

  const replies = log.replies || [];

  return (
    <Card className="bg-card" data-testid={`logres-log-${log.log_id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary" className="text-[10px] gap-1">
                <ActivityIcon type={log.activity_type} className="w-3 h-3" />
                {(log.activity_type || 'other').replace(/_/g, ' ')}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {log.from_role || 'Unknown'}{log.to_role ? ` → ${log.to_role}` : ''}
              </span>
              <span className="text-[10px] text-muted-foreground">·</span>
              <span className="text-[10px] text-muted-foreground">{formatTime(log.utc_time, timezone)}</span>
            </div>
            <p className="text-sm mt-2 whitespace-pre-wrap">{log.message}</p>
            {(log.attachment_ids || []).length > 0 && (
              <div className="mt-2 flex gap-2 flex-wrap">
                {log.attachment_ids.map((fid) => (
                  <a key={fid} href={getFileUrl(fid)} target="_blank" rel="noreferrer"
                    className="text-xs underline text-primary inline-flex items-center gap-1">
                    <Paperclip className="w-3 h-3" /> {fid.slice(0, 14)}…
                  </a>
                ))}
              </div>
            )}
            <p className="text-[10px] text-muted-foreground mt-2">by {log.created_by_name || log.created_by}</p>
          </div>
          {!expanded && (
            <Button size="sm" variant="ghost" className="h-7 text-xs shrink-0"
              onClick={() => setExpanded(true)} data-testid={`logres-reply-toggle-${log.log_id}`}>
              <MessageSquare className="w-3 h-3 mr-1" /> Reply
              {replies.length > 0 && <span className="ml-1 text-primary">({replies.length})</span>}
            </Button>
          )}
        </div>

        {expanded && (
          <div className="border-t pt-3 space-y-2">
            {replies.map((r) => (
              <div key={r.reply_id} className="pl-3 border-l-2 border-primary/40 py-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-medium">{r.user_name || r.user_id}</p>
                  <p className="text-[10px] text-muted-foreground">{formatTime(r.created_at, timezone)}</p>
                </div>
                <p className="text-sm mt-1 whitespace-pre-wrap">{r.message}</p>
                {(r.attachments || []).length > 0 && (
                  <div className="mt-1 flex gap-2 flex-wrap">
                    {r.attachments.map((fid) => (
                      <a key={fid} href={getFileUrl(fid)} target="_blank" rel="noreferrer"
                        className="text-xs underline text-primary inline-flex items-center gap-1">
                        <Paperclip className="w-3 h-3" /> attachment
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <form onSubmit={submitReply} className="space-y-2">
              <Textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Reply to this log entry…"
                rows={2}
                className="text-sm"
                data-testid={`logres-reply-input-${log.log_id}`}
              />
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1">
                  <Input
                    type="file"
                    multiple
                    accept={UPLOAD_ACCEPT_ATTR}
                    onChange={(e) => setReplyFiles(Array.from(e.target.files || []))}
                    className="h-8 text-xs max-w-xs"
                  />
                  <UploadHint className="mt-1" />
                </div>
                <Button type="submit" size="sm" disabled={sending || !replyText.trim()}
                  data-testid={`logres-reply-send-${log.log_id}`}>
                  {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                  Send Reply
                </Button>
              </div>
            </form>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Inject response card (links back to the inject for the full thread)
// ──────────────────────────────────────────────────────────────────────
function ResponseCard({ response, inject, getFileUrl, onOpenSource, timezone }) {
  return (
    <Card className="bg-card" data-testid={`logres-response-${response.response_id}`}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="text-[10px] capitalize" variant="outline">Inject Response</Badge>
              {response.sender_role && (
                <Badge variant="outline" className="text-[10px] capitalize">
                  {response.sender_role}
                </Badge>
              )}
              <span className="text-[10px] text-muted-foreground">{formatTime(response.submitted_at, timezone)}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Re: <span className="font-medium">{inject?.title || 'Unknown inject'}</span>
            </p>
            <p className="text-sm mt-2 whitespace-pre-wrap">{response.response_text}</p>
            {!!(response.attachments || []).length && (
              <div className="mt-2 flex gap-2 flex-wrap">
                {(response.attachments_meta?.length
                  ? response.attachments_meta
                  : (response.attachments || []).map((fid) => ({ file_id: fid }))).map((att) => {
                  const fileId = att.file_id || att;
                  const filename = att.filename || fileId;
                  return (
                    <a key={fileId} href={getFileUrl(fileId)} target="_blank" rel="noreferrer"
                      className="text-xs underline text-primary inline-flex items-center gap-1">
                      <Paperclip className="w-3 h-3" /> {filename}
                    </a>
                  );
                })}
              </div>
            )}
            <p className="text-[10px] text-muted-foreground mt-2">
              by {response.user_name || 'Unknown'}
            </p>
          </div>
          {inject && (
            <Button size="sm" variant="outline" className="h-7 text-xs shrink-0"
              onClick={() => onOpenSource?.(inject)}
              data-testid={`logres-response-open-${response.response_id}`}
              title="Open inject thread">
              <ExternalLink className="w-3 h-3 mr-1" /> Thread
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Main tab component
// ──────────────────────────────────────────────────────────────────────
export function LogsResponsesTab({
  exerciseId, exercise, responses, injects, openResponseDialog, getFileUrl,
}) {
  const timezone = exercise?.timezone || 'UTC';
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | logs | responses

  const loadLogs = async () => {
    setLoading(true);
    try {
      const data = await activityLogsApi.getAll(exerciseId, 'manual', 500);
      setLogs(Array.isArray(data) ? data : []);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (exerciseId) loadLogs();
    // eslint-disable-next-line
  }, [exerciseId]);

  // Live updates: new logs + replies coming over WebSocket
  useEffect(() => {
    const handler = (event) => {
      const data = event.detail;
      if (!data) return;
      if (data.type === 'activity_log_created') {
        loadLogs();
      }
      if (data.type === 'activity_log_reply' && data.log_id) {
        setLogs((prev) => prev.map((l) => l.log_id === data.log_id
          ? { ...l, replies: [...(l.replies || []), data.reply] }
          : l));
      }
    };
    window.addEventListener('ws-message', handler);
    return () => window.removeEventListener('ws-message', handler);
    // eslint-disable-next-line
  }, [exerciseId]);

  const handleReplyAdded = (logId, reply) => {
    setLogs((prev) => prev.map((l) => l.log_id === logId
      ? { ...l, replies: [...(l.replies || []), reply] }
      : l));
  };

  // Merge logs + responses into one chronologically sorted stream
  const items = useMemo(() => {
    const ls = (logs || []).map((l) => ({
      kind: 'log',
      key: `log-${l.log_id}`,
      ts: l.utc_time || l.scenario_time,
      log: l,
      searchText: `${l.message || ''} ${l.from_role || ''} ${l.to_role || ''} ${l.activity_type || ''}`.toLowerCase(),
    }));
    const rs = (responses || []).map((r) => ({
      kind: 'response',
      key: `resp-${r.response_id}`,
      ts: r.submitted_at,
      response: r,
      inject: injects.find((i) => i.inject_id === r.inject_id),
      searchText: `${r.response_text || ''} ${r.user_name || ''} ${(injects.find((i) => i.inject_id === r.inject_id)?.title) || ''}`.toLowerCase(),
    }));
    let merged = [...ls, ...rs];
    if (filter === 'logs') merged = merged.filter((x) => x.kind === 'log');
    if (filter === 'responses') merged = merged.filter((x) => x.kind === 'response');
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      merged = merged.filter((x) => x.searchText.includes(q));
    }
    merged.sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0));
    return merged;
  }, [logs, responses, injects, filter, search]);

  return (
    <div className="space-y-4" data-testid="logs-responses-tab">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <CardTitle className="font-secondary text-base">Logs &amp; Responses</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                All participant logs and inject responses for this exercise. Reply inline to keep the audit trail in one place.
              </p>
            </div>
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList className="h-8" data-testid="logres-filter-tabs">
                <TabsTrigger value="all" className="text-xs px-3 h-7">
                  All <span className="ml-1 text-muted-foreground">({logs.length + responses.length})</span>
                </TabsTrigger>
                <TabsTrigger value="logs" className="text-xs px-3 h-7">
                  Logs <span className="ml-1 text-muted-foreground">({logs.length})</span>
                </TabsTrigger>
                <TabsTrigger value="responses" className="text-xs px-3 h-7">
                  Responses <span className="ml-1 text-muted-foreground">({responses.length})</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Filter className="w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Search logs and responses…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-xs max-w-sm"
              data-testid="logres-search-input"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-muted-foreground text-sm">
              <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-sm">No logs or responses yet</p>
              <p className="text-xs mt-1">
                Participants can post entries from the right-side <strong>Log Entry</strong> panel.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item) => item.kind === 'log' ? (
                <ActivityLogCard
                  key={item.key}
                  log={item.log}
                  timezone={timezone}
                  getFileUrl={getFileUrl}
                  onReplyAdded={handleReplyAdded}
                />
              ) : (
                <ResponseCard
                  key={item.key}
                  response={item.response}
                  inject={item.inject}
                  timezone={timezone}
                  getFileUrl={getFileUrl}
                  onOpenSource={openResponseDialog}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
